import React, { useState } from 'react';
import { ExternalLink, Github } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { projects } from '../mock';

const Projects = () => {
  const [hoveredProject, setHoveredProject] = useState(null);

  return (
    <section id="projects" className="bg-[#0a0a0a] py-32 px-6">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-5xl md:text-6xl font-bold text-center text-white mb-8 drop-shadow-[0_0_20px_rgba(59,130,246,0.3)]">
          Featured Projects
        </h2>
        <p className="text-xl text-gray-400 text-center mb-20 max-w-3xl mx-auto">
          Transforming ideas into intelligent solutions
        </p>

        <div className="space-y-32">
          {projects.map((project, index) => (
            <div
              key={project.id}
              className="min-h-[70vh] flex items-center"
              onMouseEnter={() => setHoveredProject(project.id)}
              onMouseLeave={() => setHoveredProject(null)}
            >
              <Card className="w-full bg-gradient-to-br from-[#0f0f1a] to-[#1a1a2e] border-gray-800 hover:border-blue-500/50 p-12 transition-all duration-500 hover:shadow-[0_0_40px_rgba(59,130,246,0.2)] backdrop-blur-sm">
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  {/* Project visual */}
                  <div className={`order-${index % 2 === 0 ? '1' : '2'}`}>
                    <div className={`relative h-80 rounded-xl bg-gradient-to-br ${project.gradient} p-1 overflow-hidden group`}>
                      <div className="w-full h-full bg-[#0a0a0a] rounded-lg flex items-center justify-center">
                        <div className="text-center p-8">
                          <div className={`text-8xl font-bold bg-gradient-to-br ${project.gradient} bg-clip-text text-transparent mb-4 drop-shadow-[0_0_20px_rgba(59,130,246,0.4)]`}>
                            {project.id}
                          </div>
                          <div className={`text-2xl font-semibold bg-gradient-to-r ${project.gradient} bg-clip-text text-transparent`}>
                            {project.title.split(' ').slice(0, 2).join(' ')}
                          </div>
                        </div>
                      </div>
                      {/* Animated border */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                    </div>
                  </div>

                  {/* Project details */}
                  <div className={`order-${index % 2 === 0 ? '2' : '1'}`}>
                    <div className="space-y-6">
                      <div>
                        <p className="text-sm text-cyan-400 font-semibold mb-2 tracking-wider uppercase">
                          {project.subtitle}
                        </p>
                        <h3 className="text-4xl font-bold text-white mb-4">
                          {project.title}
                        </h3>
                      </div>

                      <p className="text-lg text-gray-400 leading-relaxed">
                        {project.description}
                      </p>

                      <p className="text-gray-500 leading-relaxed">
                        {project.fullDescription}
                      </p>

                      {/* Tech stack */}
                      <div className="flex flex-wrap gap-2">
                        {project.technologies.map((tech, idx) => (
                          <Badge
                            key={idx}
                            variant="outline"
                            className="px-3 py-1 border-blue-500/30 text-blue-300 hover:bg-blue-500/10 transition-colors backdrop-blur-sm"
                          >
                            {tech}
                          </Badge>
                        ))}
                      </div>

                      {/* Action buttons */}
                      <div className="flex gap-4 pt-4">
                        {project.hasGithub ? (
                          <Button
                            asChild
                            className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white transition-all duration-300 hover:scale-105 shadow-[0_0_15px_rgba(59,130,246,0.3)] hover:shadow-[0_0_25px_rgba(59,130,246,0.5)]"
                          >
                            <a href={project.githubUrl} target="_blank" rel="noopener noreferrer">
                              <Github className="mr-2 w-4 h-4" />
                              View on GitHub
                            </a>
                          </Button>
                        ) : (
                          <Button
                            disabled
                            className="bg-gray-700 text-gray-400 cursor-not-allowed opacity-50"
                          >
                            <Github className="mr-2 w-4 h-4" />
                            Private Project
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          className="border-blue-500/50 hover:border-blue-400 text-blue-300 hover:bg-blue-500/10 transition-all duration-300"
                        >
                          <ExternalLink className="mr-2 w-4 h-4" />
                          Learn More
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;